import { defineBlock, smartProps, useChildrenBlocks } from '@smartfire/js-block-engine'
import style from './block.module.scss'
import { useSmartForm } from '@smartfire/wordpress-smartforms'
import { useCallback, useEffect, useRef, useState } from 'react'
import apiFetch from '@wordpress/api-fetch'
import Alert from '@mui/material/Alert'
import { useTranslation } from 'react-i18next'

const TEMPLATE = [
    ['smartfire/contact---form---form-content---two-columns'],
    ['smartfire/contact---form---form-content---two-columns'],
    ['smartfire/contact---form---form-content---select'],
    ['smartfire/contact---form---form-content---textarea'],
]

const ALLOWED_BLOCKS = [
    'smartfire/contact---form---form-content---input',
    'smartfire/contact---form---form-content---recaptcha',
    'smartfire/contact---form---form-content---select',
    'smartfire/contact---form---form-content---textarea',
    'smartfire/contact---form---form-content---two-columns',
    'smartfire/contact---form---form-content---checkbox',
    'smartfire/contact---form---form-content---submit',
]

async function submitForm(formData) {
    try {
        const result = await apiFetch({
            path: 'smartforms/v1/submit',
            method: 'POST',
            body: formData,
        })
        return {
            success: true,
            message: result.message,
        }
    } catch (e) {
        if (e.success === false) {
            return {
                success: false,
                errors: e.errors,
            }
        } else {
            throw e
        }
    }
}

function FormContent() {
    const { t } = useTranslation()
    const { Form } = useSmartForm({ smartProp: 'contactForm' })
    const { Children } = useChildrenBlocks()
    const [submitMessage, setSubmitMessage] = useState({})
    // Recaptcha
    const [reCaptcha, setReCaptcha] = useState()
    const domRef = useRef()

    // Add method "getValueFromChildren" to document
    useEffect(() => {
        if (domRef.current) {
            domRef.current.getValueFromChildren = function (childValue) {
                setReCaptcha(childValue)
            }
        }
    }, [domRef])

    const formHandler = useCallback(
        async ({ formData }) => {
            // Recaptcha
            if (formData.has('g-recaptcha-response') && reCaptcha) {
                let token = ''
                if (reCaptcha.props.size === 'invisible') {
                    token = await reCaptcha.executeAsync()
                } else {
                    do {
                        token = reCaptcha && reCaptcha.getValue()
                    } while (reCaptcha === undefined)
                }
                reCaptcha.reset()
                formData.set('g-recaptcha-response', token)
            }

            const r = await submitForm(formData)
            if (r.success) {
                setSubmitMessage({ success: true, message: r.message })
            } else {
                setSubmitMessage({ success: false, message: r.errors[0].message })
            }
            return r
        },
        [reCaptcha],
    )

    return (
        <div className={style.form} ref={domRef}>
            <Form onSmartFormSubmit={formHandler}>
                <Children
                    gutenbergEditorConfig={{
                        allowedBlocks: ALLOWED_BLOCKS,
                        template: TEMPLATE,
                        templateLock: false,
                    }}
                    className={style.formContent}
                />
            </Form>
            <p className={style.requiredFields}>{`* ${t('Champs obligatoires')}`}</p>
            {typeof submitMessage.success !== 'undefined' && (
                <Alert
                    severity={submitMessage?.success ? 'success' : 'error'}
                    className={style.alert}
                >
                    {submitMessage?.success
                        ? t('Merci ! Votre demande a bien été envoyée.')
                        : submitMessage?.message}
                </Alert>
            )}
        </div>
    )
}

export default defineBlock({
    Block: FormContent,
    smartProps: {
        contactForm: smartProps.object.withDefault({
            configuration: {
                emailTo: 'contact@test.fr',
                subject: '[subject]',
                headers: 'From: [firstname] [lastname] <[email]>',
                message: '[message]',
            },
        }),
    },
    setup: {
        title: 'Form content',
        parent: ['smartfire/contact---form'],
    },
})
